import VideoHelper from './VideoHelper';
import CookieHelper from './CookieHelper';

class YoutubeHelper {
    constructor() {
        this.youtubeApiLoaded = false;
    }

    /**
     * create youtube player
     * @param videoSettings
     * @returns {YT.Player}
     */
    static createPlayer(videoSettings) {
        const settings = {
            rel: 0,
            loop: 1,
            playsinline: 1,
            enablejsapi: 1,
            origin: window.location.href,
            mute: !videoSettings.audio,
            volume: 0,
        };

        if (videoSettings.hideControls) {
            settings.controls = 0;
            settings.showinfo = 0; // Hide the video title
            settings.wmode = 'transparent';
            settings.modestbranding = 1; // Hide the Youtube Logo
            settings.playlist = videoSettings.youtubeId;
            settings.fs = 0; // Hide the full screen button
            settings.autohide = 1; // Hide video controls when playing
            settings.autoplay = 1;
        }

        return new YT.Player(videoSettings.wrapperID, { // eslint-disable-line no-undef, no-unused-vars
            videoId: videoSettings.youtubeId,
            // host: 'https://www.youtube-nocookie.com',
            playerVars: settings,
            events: {
                onReady: (event) => {
                    if (!videoSettings.audio) {
                        VideoHelper.stopAll(videoSettings.wrapperID);
                        event.target.playVideo();
                        event.target.mute();
                    }
                },
                onStateChange: (event) => {
                    if (event && event.data === 1) { // playing
                        const videoHolder = document.getElementById(videoSettings.wrapperID);
                        if (videoHolder && videoHolder.id) {
                            const videoElement = videoHolder.parentElement.parentElement;
                            videoElement.classList.remove('video--loading', 'video--loading-animation');
                        }
                    } else if (event && event.data === 0) { // ended
                        event.target.playVideo();
                    } else if (event && event.data === -1) { // not started
                        if (!videoSettings.audio) {
                            event.target.mute();
                        }
                    }
                },
            },
        });
    }

    /**
     * add video to element and start video
     * @param videoSettings
     */
    static addAndAutoplayVideo(videoSettings) {
        const apiReady = document.body.getAttribute('data-youtube-api-ready');

        if (typeof apiReady !== typeof undefined && apiReady !== null && apiReady !== false) {
            YoutubeHelper.createPlayerContainer(videoSettings);
        } else {
            YoutubeHelper.addYoutubeIframeApi();
            window.onYouTubeIframeAPIReady = () => {
                document.body.setAttribute('data-youtube-api-ready', 'true');

                YoutubeHelper.createPlayerContainer(videoSettings);
            };
        }
    }

    static createPlayerContainer(videoSettings) {
        const videoContainer = videoSettings.container;
        const playerID = videoContainer.id;
        videoContainer.innerHTML = '';

        videoSettings.wrapperID = playerID;

        const player = YoutubeHelper.createPlayer(videoSettings);
        window.youtubePlayers[playerID] = player;
    }

    /**
     * add video to element and start video
     * @param container
     * @param youtubeId
     * @param wrapperID
     */
    addAndAutoplayLightboxVideo(container, youtubeId, wrapperID) {
        let player = {};
        const apiReady = document.body.getAttribute('data-youtube-api-ready');
        const lightboxContent = document.getElementsByClassName(container)[0];

        if (typeof apiReady !== typeof undefined && apiReady !== null && apiReady !== false) {
            player = this.createLightboxPlayerContent(lightboxContent, wrapperID, youtubeId);
        } else {
            this.addYoutubeIframeApi();
            window.onYouTubeIframeAPIReady = () => {
                document.body.setAttribute('data-youtube-api-ready', 'true');

                player = this.createLightboxPlayerContent(lightboxContent, wrapperID, youtubeId);
            };
        }
        return player;
    }

    createLightboxPlayerContent(lightboxContent, wrapperID, youtubeId) {
        const videoContainer = document.createElement('div');

        videoContainer.id = wrapperID;
        lightboxContent.append(videoContainer);

        const player = this.createPlayer(wrapperID, youtubeId);

        window.lightboxPlayer = player;

        // window.addEventListener('lightboxclose', YoutubeHelper.stopAndDestroyPlayer);

        return player;
    }

    /**
     * get YouTube ID from various YouTube URL
     * @param url
     * @returns {*}
     */
    static youTubeGetID(url) {
        url = url.replace(/(>|<)/gi, '').split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
        let ID = url;

        if (url[2] !== undefined) {
            ID = url[2].split(/[^0-9a-z_-]/i);
            [ID] = ID;
        }
        return ID;
    }

    /**
     * add script tag to DOM as first script in footer
     */
    static addYoutubeIframeApi() {
        if (!this.youtubeApiLoaded) {
            const tag = document.createElement('script');

            tag.src = 'https://www.youtube.com/iframe_api';
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        }
        this.youtubeApiLoaded = true;
    }

    /**
     * stop all youtube players on the page
     * that have not been destroyed
     */
    static stopAll() {
        if (window.youtubePlayers.length) {
            for (let i = 0; i < window.youtubePlayers.length; i += 1) {
                if (window.youtubePlayers[0].a !== null) {
                    window.youtubePlayers[0].pauseVideo();
                }
            }
        }
    }

    /**
     * add video consent layer
     * @param video
     */
    static activateConsent(video) {
        if (video.classList.contains('video--loading')) {
            video.classList.remove('video--consent-accepted');
        }
    }

    /**
     * remove video consent layer
     * @param video
     */
    static displayYoutubeVideo(video) {
        video.classList.add('video--consent-accepted');
    }

    /**
     * dispatch event if consent for Youtube was given
     */
    static dispatchConsentEvent() {
        const youtubeAllowedEvent = new CustomEvent('consent-type-youtube');
        window.dispatchEvent(youtubeAllowedEvent);
    }

    static displayAllYoutubeVideos() {
        CookieHelper.setCookie('consent-type-youtube', true);
        const videos = document.querySelectorAll('.video');
        if (videos) {
            for (const video of videos) {
                const videoUrl = video.getAttribute('data-embed');
                if (videoUrl && videoUrl.length) {
                    if (videoUrl.indexOf('youtube') >= 0) {
                        YoutubeHelper.displayYoutubeVideo(video);
                    }
                }
            }
        }
        window.removeEventListener('consent-type-youtube', YoutubeHelper.displayAllYoutubeVideos);
        window.addEventListener('consent-withdraw-youtube', YoutubeHelper.activateAllYoutubeConsents);
    }

    static activateAllYoutubeConsents() {
        CookieHelper.clearCookie('consent-type-youtube');
        const videos = document.querySelectorAll('.video');
        if (videos) {
            for (const video of videos) {
                const videoUrl = video.getAttribute('data-embed');
                if (videoUrl && videoUrl.length) {
                    if (videoUrl.indexOf('youtube') >= 0) {
                        YoutubeHelper.activateConsent(video);
                    }
                }
            }
        }
        window.addEventListener('consent-type-youtube', YoutubeHelper.displayAllYoutubeVideos);
        window.removeEventListener('consent-withdraw-youtube', YoutubeHelper.activateAllYoutubeConsents);
    }

    /**
     * add listener for custom event and display all videos on the current page
     */
    static addYoutubeConsentListener() {
        window.addEventListener('consent-type-youtube', YoutubeHelper.displayAllYoutubeVideos, false);
        window.addEventListener('consent-withdraw-youtube', YoutubeHelper.activateAllYoutubeConsents);
    }
}

export default YoutubeHelper;
